import { t } from '@lingui/core/macro';
import { createLazyRoute } from '@tanstack/react-router';
import { queryClient, useCreateMutation, useIndexQuery } from 'api';
import { Banner } from 'components/banner/banner';
import { CenteredSpinner } from 'components/CenteredSpinner';
import { ErrorIconMUI } from 'components/icons/ErrorIconMUI';
import PageContainer from 'components/PageContainer';
import { useCurrentRole, useCurrentRoleOrThrow } from 'hooks/slices';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { WithFeatureAccess } from 'routes/routeUtils';
import type { SetPanelistConnectionParams } from 'types/api/pharmacies/panelist_connection';
import { ConnectPanelistCard } from '../ConnectPanelistCard';
import { PanelistConnectedCard } from '../PanelistConnectedCard';
import { PanelistConnectionStatusIcon } from '../PanelistStatusComponents';

export const useGetPanelistConnectionStatus = () => {
  const currentRole = useCurrentRole();

  const { data, isPending } = useIndexQuery(
    'pharmacies/panelist_connection/_status',
    {},
    { enabled: currentRole?.resource_type === 'Pharmacy' }
  );

  return {
    panelistConnection: data,
    isPharmacyAllowedToEstablishConnection: !!data?.is_lgo_connection_allowed,
    panelist: data?.pharmacy_panelist?.panelist,
    isPending,
  };
};

const PharmacyPanelistConnectionPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const currentRole = useCurrentRoleOrThrow();

  const setPanelistConnectionMutation = useCreateMutation('pharmacies/panelist_connection/_set');
  const resetPanelistConnection = useCreateMutation('pharmacies/panelist_connection/_reset');

  const { panelistConnection, isPharmacyAllowedToEstablishConnection } =
    useGetPanelistConnectionStatus();

  const onSetPanelistConnectionHandler = (
    panelistConnectionParams: SetPanelistConnectionParams
  ) => {
    setIsLoading(true);

    setPanelistConnectionMutation
      .mutateAsync(panelistConnectionParams)
      .then(() => {
        toast.warning(t`La connexion avec votre extracteur est en cours d’établissement !`);
        queryClient.invalidateQueries({ queryKey: ['pharmacies/panelist_connection/_status'] });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onResetPanelistConnectionHandler = () => {
    setIsLoading(true);

    resetPanelistConnection
      .mutateAsync({} as never)
      .then(() => {
        toast.success(t`Votre extracteur a été déconnecté avec succès !`);
        queryClient.invalidateQueries({ queryKey: ['pharmacies/panelist_connection/_status'] });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isUserNotPharmacyOwner = currentRole.kind !== 'pharmacy_owner';

  if (!panelistConnection) return <CenteredSpinner />;

  return (
    <PageContainer
      pageTitle={t`Connexion extracteur`}
      pageTitleBadge={
        <PanelistConnectionStatusIcon panelistConnection={panelistConnection} fontSize="medium" />
      }
    >
      {!isPharmacyAllowedToEstablishConnection ? (
        <Banner
          icon={<ErrorIconMUI fontSize="medium" sx={{ marginRight: 1 }} />}
          title={t`Vous n’êtes pas autorisé à connecter votre panéliste à Faks pour activer la remontée automatique des données de ventes sur vos opérations commerciales. Merci de contacter votre groupement pour en savoir plus.`}
          color="error"
        />
      ) : (
        <>
          {isUserNotPharmacyOwner && (
            <Banner
              title={t`Seul le titulaire de la pharmacie peut déconnecter ou modifier la connexion extracteur.`}
              color="info"
            />
          )}
          {panelistConnection?.pharmacy_panelist?.panelist ? (
            <PanelistConnectedCard
              panelistConnection={panelistConnection}
              handleOnReset={onResetPanelistConnectionHandler}
              disableDisconnectButton={isUserNotPharmacyOwner}
              isLoading={isLoading}
            />
          ) : (
            <ConnectPanelistCard
              handleOnSet={onSetPanelistConnectionHandler}
              disableConnectButton={isUserNotPharmacyOwner}
              isLoading={isLoading}
            />
          )}
        </>
      )}
    </PageContainer>
  );
};

export const Route = createLazyRoute('/pharmacy/settings/extractor')({
  component: () => {
    const { isPharmacyAllowedToEstablishConnection, isPending } = useGetPanelistConnectionStatus();
    const access = isPending
      ? 'loading'
      : isPharmacyAllowedToEstablishConnection
        ? 'granted'
        : 'denied';
    return WithFeatureAccess(access, <PharmacyPanelistConnectionPage />);
  },
});
